/* eslint-disable no-underscore-dangle */
import { toast } from 'react-toastify';
import {
  GET_BLOCKLIST,
  GET_BLOCKLIST_INDEX,
  ADD_BLOCKLIST_ENTRY,
  REMOVE_BLOCKLIST_ENTRY,
  LOOKUP_BLOCKLIST_NUMBER,
  UPDATE_BLOCKLIST_SETTINGS
} from '../action/constant';
import { DEFAULT_COUNTRY_CODE } from '../utils/phone';

const emptyLookup = {
  loading: false,
  done: false,
  found: false,
  isBlocked: false,
  visitor: null,
  phone: '',
  err: null
};

const initialState = {
  loading: false,
  entries: [],
  total: 0,
  page: 1,
  err: {},

  adding: false,
  addErr: null,

  removingId: null,

  lookup: emptyLookup,

  // Active entries keyed by their normalised phone, so a visitor row can be
  // matched without a lookup call per row.
  index: {},

  confirm: {
    open: false,
    mode: 'allow',
    phone: '',
    name: '',
    entryId: null,
    payload: null
  },

  addModal: {
    open: false,
    phone: '',
    visitorRecordId: null,
    autoLookup: false
  },

  settings: {
    blocklist: false,
    defaultCountryCode: DEFAULT_COUNTRY_CODE,
    notifyEmails: []
  },

  savingSettings: false
};

/** Reads the blocklist settings off the building cached at login / select. */
function settingsFromStorage(current) {
  try {
    const building = JSON.parse(localStorage.getItem('building')) || {};
    const notify = building.blocklistNotify || {};
    return {
      blocklist: Boolean(building.blocklist),
      defaultCountryCode: building.defaultCountryCode || DEFAULT_COUNTRY_CODE,
      notifyEmails: Array.isArray(notify.emails) ? notify.emails : []
    };
  } catch (e) {
    return current;
  }
}

export default function blocklistReducer(state = initialState, action) {
  switch (action.type) {
    case 'BLOCKLIST_SETTINGS_LOAD':
      return {
        ...state,
        settings: settingsFromStorage(state.settings)
      };

    case GET_BLOCKLIST_INDEX.SUCCESS: {
      const index = {};
      (action.data.entries || []).forEach(entry => {
        index[entry.phone] = entry;
      });
      return { ...state, index };
    }

    case GET_BLOCKLIST_INDEX.ERROR:
      // A failed index only means the table shows Block everywhere; the API
      // still refuses a duplicate, so this is not worth a toast.
      return state;

    case 'BLOCKLIST_CONFIRM_OPEN':
      return {
        ...state,
        confirm: {
          open: true,
          mode: action.data.mode,
          phone: action.data.phone || '',
          name: action.data.name || '',
          entryId: action.data.entryId || null,
          payload: action.data.payload || null
        }
      };

    case 'BLOCKLIST_CONFIRM_CLOSE':
      return {
        ...state,
        confirm: { ...initialState.confirm }
      };

    case 'BLOCKLIST_ADD_MODAL_OPEN':
      return {
        ...state,
        addModal: {
          open: true,
          phone: action.data.phone || '',
          visitorRecordId: action.data.visitorRecordId || null,
          autoLookup: Boolean(action.data.phone)
        },
        lookup: emptyLookup,
        addErr: null
      };

    case 'BLOCKLIST_ADD_MODAL_CLOSE':
      return {
        ...state,
        addModal: { ...initialState.addModal },
        lookup: emptyLookup,
        addErr: null
      };

    case UPDATE_BLOCKLIST_SETTINGS.PENDING:
      return {
        ...state,
        savingSettings: true
      };

    case UPDATE_BLOCKLIST_SETTINGS.SUCCESS:
      toast.dismiss();
      toast.success('Blocklist settings saved');
      return {
        ...state,
        savingSettings: false,
        settings: { ...state.settings, ...action.data }
      };

    case UPDATE_BLOCKLIST_SETTINGS.ERROR:
      toast.dismiss();
      toast.error(action.err.message);
      return {
        ...state,
        savingSettings: false
      };

    case 'BLOCKLIST_LOOKUP_RESET':
      return {
        ...state,
        lookup: emptyLookup,
        addErr: null
      };

    case 'BLOCKLIST_ADD_ERROR_CLEAR':
      return {
        ...state,
        addErr: null
      };

    /* ---------------------------------------------------------------- list */

    case GET_BLOCKLIST.PENDING:
      return {
        ...state,
        loading: true,
        err: {}
      };

    case GET_BLOCKLIST.SUCCESS:
      return {
        ...state,
        loading: false,
        entries: action.data.entries || [],
        total: action.data.total || 0,
        page: action.data.page || 1,
        err: {}
      };

    case GET_BLOCKLIST.ERROR:
      return {
        ...state,
        loading: false,
        err: action.err
      };

    /* ----------------------------------------------------------------- add */

    case ADD_BLOCKLIST_ENTRY.PENDING:
      return {
        ...state,
        adding: true,
        addErr: null
      };

    case ADD_BLOCKLIST_ENTRY.SUCCESS:
      toast.dismiss();
      toast.success('Number blocked');
      return {
        ...state,
        adding: false,
        addErr: null,
        lookup: emptyLookup,
        addModal: { ...initialState.addModal },
        entries: [ action.data, ...state.entries ],
        index: { ...state.index, [action.data.phone]: action.data },
        total: state.total + 1
      };

    // A 409 is not a failure to show as a toast: the modal renders the existing
    // entry, which the API returns in `data`, so the admin can see who blocked
    // the number and when.
    case ADD_BLOCKLIST_ENTRY.ERROR:
      if (action.err.status !== 409) {
        toast.dismiss();
        toast.error(action.err.message);
      }
      return {
        ...state,
        adding: false,
        addErr: action.err
      };

    /* -------------------------------------------------------------- remove */

    case REMOVE_BLOCKLIST_ENTRY.PENDING:
      return {
        ...state,
        removingId: action.data.entryId
      };

    case REMOVE_BLOCKLIST_ENTRY.SUCCESS: {
      toast.dismiss();
      toast.success('Number allowed');
      const index = { ...state.index };
      Object.keys(index).forEach(phone => {
        if (index[phone]._id === action.data._id) delete index[phone];
      });
      return {
        ...state,
        removingId: null,
        entries: state.entries.filter(entry => entry._id !== action.data._id),
        index,
        total: state.total > 0 ? state.total - 1 : 0
      };
    }

    case REMOVE_BLOCKLIST_ENTRY.ERROR:
      toast.dismiss();
      toast.error(action.err.message);
      return {
        ...state,
        removingId: null,
        err: action.err
      };

    /* -------------------------------------------------------------- lookup */

    case LOOKUP_BLOCKLIST_NUMBER.PENDING:
      return {
        ...state,
        lookup: {
          ...emptyLookup,
          loading: true,
          phone: action.data.phone
        },
        addErr: null
      };

    // `found: false` is a supported answer, not an error — a number with no
    // visit history can still be blocked.
    case LOOKUP_BLOCKLIST_NUMBER.SUCCESS:
      return {
        ...state,
        lookup: {
          loading: false,
          done: true,
          found: Boolean(action.data.found),
          isBlocked: Boolean(action.data.isBlocked),
          visitor: action.data.visitor || null,
          phone: action.data.phone || '',
          err: null
        }
      };

    case LOOKUP_BLOCKLIST_NUMBER.ERROR:
      return {
        ...state,
        lookup: {
          ...emptyLookup,
          done: false,
          err: action.err
        }
      };

    default:
      return state;
  }
}
